import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import ErrorBoundary from "@/app/shared/ErrorBoundary";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { HELP_TIMETABLES } from "@/constants/HelpUrls";
import { NEW_TIMETABLE } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import { string } from "@/utils/hooks/endorse";
import useGoTo from "@/utils/hooks/useGoTo";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viGear, viLocationPinDot, viTableClock } from "@sippy-platform/valkyrie";

import { Box, Grid, IconButton, List, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import DestinationList from "./DestinationList";
import DestinationPicker from "./DestinationPicker";
import HolidaySetItem from "./holidaySets/HolidaySetItem";
import HolidaySets from "./holidaySets/HolidaySets";
import Roster from "./Roster";
import RuleEditor from "./ruleEditor/RuleEditor";
import timetableReducer from "./timetableReducer";

function Timetable() {
  const { id, pbxId } = useParams();
  const { getGoTo } = useGoTo();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesLg = useMediaQuery(theme.breakpoints.down("lg"));

  const pbx = useSelector((state) => state.domain.pbx);
  const timetableStore = useSelector((state) => state.domain.timetables);

  const [activeDestination, setActiveDestination] = useState(null);
  const [tab, setTab] = useState(() => (id ? 0 : 2));
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setTab(id ? 0 : 2);
  }

  const handleTab = (event, newTab) => setTab(newTab);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_TIMETABLES));
  }, [dispatch]);

  // Fetch the timetable, but only if an ID is given
  useEffect(() => {
    Promise.resolve(Api.getHolidaySets());
  }, []);

  const {
    value: timetable,
    dispatch: timetableDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_TIMETABLE,
    reducer: timetableReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." })
        .truthy(
          !timetableStore.list.find(
            (_timetable) => timetable.name === _timetable.name && timetable.id !== _timetable.id,
          ),
          { message: "A timetable with this name already exists." },
        )
        .pattern("^[a-zA-Z0-9-_ ]*$", {
          message: "Name should only contain alphanumerical characters, dashes and underscores.",
        }),
    }),
    onSubmit: () => Api.storeTimetable(timetable),
    onSuccess: (response) => {
      if (!timetable.id) {
        dispatch({ type: "id", value: response.data.timetable.id });
        navigate(`${response.data.timetable.id}`);
      }

      Promise.resolve(Api.getTimetables());
    },
  });

  useEffect(() => {
    if (id) {
      toggleLoadingState(true);

      Promise.resolve(Api.getTimetable({ id }))
        .then((_response) => timetableDispatch({ type: "set", value: _response.data.timetable }))
        .finally(toggleLoadingState);
    }
  }, [id, timetableDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    timetableDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    navigate(`/pbx/${pbxId}/call-flow/timetables`);
  }

  return (
    <Page maxWidth="xxl">
      <title>{`${timetable.name || "Timetable"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "Timetable", icon: viTableClock, id: "timetable", disabled: !timetable.id },
            { label: "Destinations", icon: viLocationPinDot, id: "destinations", disabled: !timetable.id },
            { label: "Settings", icon: viGear, id: "settings" },
          ]}
          avatar={
            <IconButton variant="avatar" component={NavLink} to={"../timetables"}>
              <Valkyrie icon={viArrowLeft} />
              <Avatar text={timetable.name} icon={viTableClock} />
            </IconButton>
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={timetable.id || "nk"}
                isActionable={!!timetable?.id}
                onDeleteCheck={() => Api.deleteTimetable(timetable, false)}
                onDelete={() => Api.deleteTimetable(timetable, true)}
                name={`${timetable.name}`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={timetable.name || "Unnamed timetable"}
        />
        {tab === 0 && (
          <Grid container spacing={2}>
            {!matchesLg && (
              <Grid size={{ lg: 3 }}>
                <ErrorBoundary>
                  <List>
                    <DestinationList
                      timetable={timetable}
                      activeDestination={activeDestination}
                      setActiveDestination={setActiveDestination}
                      getGoTo={getGoTo}
                    />
                  </List>
                </ErrorBoundary>
              </Grid>
            )}
            <Grid size={{ xs: 12, lg: 9 }}>
              <ErrorBoundary>
                <Roster
                  key={timetable.id}
                  timetable={timetable}
                  activeDestination={activeDestination}
                  setSaveState={setSaveState}
                  getGoTo={getGoTo}
                  timetableDispatch={timetableDispatch}
                />
              </ErrorBoundary>
              {matchesLg && (
                <ErrorBoundary>
                  <DestinationPicker
                    timetable={timetable}
                    activeDestination={activeDestination}
                    setActiveDestination={setActiveDestination}
                    getGoTo={getGoTo}
                  />
                </ErrorBoundary>
              )}
            </Grid>
          </Grid>
        )}
        {tab === 1 && (
          <ErrorBoundary>
            <RuleEditor
              key={timetable.id}
              timetable={timetable}
              timetableDispatch={timetableDispatch}
              getGoTo={getGoTo}
              setSaveState={setSaveState}
            />
          </ErrorBoundary>
        )}
        {tab === 2 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="name"
                  label="Name"
                  onChange={formHandler}
                  value={timetable.name}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup
              title="Holiday sets"
              actions={<HolidaySets timetable={timetable} timetableDispatch={timetableDispatch} />}
            >
              <Grid size={12}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                    gap: 1,
                  }}
                >
                  {timetable.holidaySets.length ? (
                    timetable.holidaySets.map((_holidaySet) => (
                      <HolidaySetItem
                        key={_holidaySet.id}
                        holidaySet={_holidaySet}
                        timetable={timetable}
                        timetableDispatch={timetableDispatch}
                      />
                    ))
                  ) : (
                    <Typography>No holiday sets linked to this timetable.</Typography>
                  )}
                </Box>
              </Grid>
            </FormGroup>
            <FormGroup title="Holiday overflow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <OverflowDrawer
                  label="Overflow"
                  value={timetable.holidayOverflow}
                  propertyName="holidayOverflow"
                  overflowHandler={(value) => formHandler("holidayOverflow", value)}
                  filter={{ exten: [timetable.exten], custom: [], sound: [] }}
                  helperText="Set the destination when a holiday is active without having its own overflow."
                />
              </Grid>
            </FormGroup>
          </>
        )}
      </Stack>
    </Page>
  );
}

export default Timetable;
